/**
 * Servicio de Usuarios
 * Conecta con el backend de autenticación para gestión de usuarios
 */

import { apiFetch } from "./apiClient";

// URL del backend de autenticación
const AUTH_API = import.meta.env.VITE_AUTH_API_URL || "http://localhost:5010/api/v1";

/**
 * Interfaz de Usuario del sistema de autenticación
 */
export interface User {
  id: string;
  username: string;
  email?: string | null;
  role_id: number;
  role_name: string;
  company_id?: string | null;
  company_name?: string | null;
  branch_id?: string | null;
  branch_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  modules_config?: Record<string, any>;
  is_active: boolean;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
  last_login?: string | null;
}

/**
 * Interfaz de Compañía
 */
export interface Company {
  id: string;
  name: string;
  rfc?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interfaz de Branch (Sucursal)
 */
export interface Branch {
  id: string;
  company_id: string;
  name: string;
  address?: string | null;
  backend_url?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interfaz de Role (Rol)
 */
export interface Role {
  id: number;
  name: string;
  description?: string | null;
}

/**
 * Datos para crear/actualizar usuario
 */
export interface UserCreateData {
  username: string;
  password: string;
  email?: string;
  role_id: number;
  company_id?: string;
  branch_id?: string;
  first_name?: string;
  last_name?: string;
  modules_config?: Record<string, any>;
  is_active?: boolean;
}

export interface UserUpdateData {
  email?: string;
  role_id?: number;
  company_id?: string;
  branch_id?: string;
  first_name?: string;
  last_name?: string;
  modules_config?: Record<string, any>;
  is_active?: boolean;
  password?: string;
}

/**
 * Filtros para listar usuarios
 */
export interface UserFilters {
  company_id?: string;
  branch_id?: string;
  role_id?: number;
  is_active?: boolean;
}

/**
 * Respuesta del API
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Servicio de gestión de usuarios
 */
class UsersService {
  /**
   * Obtener todos los usuarios (según permisos del usuario actual)
   */
  async getUsers(filters?: UserFilters): Promise<User[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters?.company_id) queryParams.set('company_id', filters.company_id);
      if (filters?.branch_id) queryParams.set('branch_id', filters.branch_id);
      if (filters?.role_id) queryParams.set('role_id', filters.role_id.toString());
      if (filters?.is_active !== undefined) queryParams.set('is_active', filters.is_active.toString());

      const url = `${AUTH_API}/users/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await apiFetch(url, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<User[]> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al obtener usuarios');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Obtener un usuario por ID
   */
  async getUserById(userId: string): Promise<User> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/${userId}`, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<User> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al obtener usuario');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  /**
   * Crear nuevo usuario
   */
  async createUser(userData: UserCreateData): Promise<User> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/`, {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result: ApiResponse<User> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al crear usuario');
      }

      return result.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Actualizar usuario existente
   */
  async updateUser(userId: string, userData: UserUpdateData): Promise<User> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result: ApiResponse<User> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al actualizar usuario');
      }

      return result.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Eliminar usuario (soft delete)
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/${userId}`, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result: ApiResponse<null> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al eliminar usuario');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Activar usuario
   */
  async activateUser(userId: string): Promise<User> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/${userId}/activate`, { method: 'PUT' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result: ApiResponse<User> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al activar usuario');
      }

      return result.data;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }

  /**
   * Desactivar usuario
   */
  async deactivateUser(userId: string): Promise<User> {
    try {
      const response = await apiFetch(`${AUTH_API}/users/${userId}/deactivate`, { method: 'PUT' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result: ApiResponse<User> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al desactivar usuario');
      }

      return result.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }

  /**
   * Obtener todos los roles del sistema
   */
  async getRoles(): Promise<Role[]> {
    try {
      const response = await apiFetch(`${AUTH_API.replace('/users', '')}/auth/roles`, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: ApiResponse<Role[]> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al obtener roles');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  /**
   * Helper: Obtener nombre completo del usuario
   */
  getFullName(user: User): string {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.username;
  }

  /**
   * Helper: Obtener iniciales del usuario
   */
  getInitials(user: User): string {
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    return user.username.charAt(0).toUpperCase();
  }
}

// Exportar instancia única
export const usersService = new UsersService();
